import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure,
  Input, Textarea, Code,
} from '@chakra-ui/react';
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { FiRefreshCw, FiPlus, FiTrash2, FiCopy } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState({ name: '', description: '' });
  const toast = useToast();

  const fetchKeys = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/api-keys/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => { setKeys(data.results || data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const createKey = async () => {
    try {
      const res = await fetch(`${API_BASE}/api-keys/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: 'API Key created', status: 'success', duration: 3000 });
      onClose();
      setForm({ name: '', description: '' });
      fetchKeys();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const deleteKey = async (id, name) => {
    if (!window.confirm(`Delete API key "${name}"?`)) return;
    try {
      await fetch(`${API_BASE}/api-keys/${id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      toast({ title: 'API Key deleted', status: 'success', duration: 3000 });
      fetchKeys();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast({ title: 'Copied to clipboard', status: 'info', duration: 2000 });
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">API Keys</Heading>
          <Text color="gray.500">{keys.length} API keys configured</Text>
        </VStack>
        <HStack>
          <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onOpen}>Create Key</Button>
          <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchKeys}>Refresh</Button>
        </HStack>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Name</Th>
              <Th>Key</Th>
              <Th>Status</Th>
              <Th>Description</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {keys.map((k) => (
              <Tr key={k.id} _hover={{ bg: 'gray.50' }}>
                <Td><Text fontWeight="medium" fontSize="sm">{k.name}</Text></Td>
                <Td>
                  <HStack>
                    <Code fontSize="xs">{k.key?.substring(0, 12)}...</Code>
                    <Button size="xs" variant="ghost" onClick={() => copyKey(k.key)}>
                      <FiCopy />
                    </Button>
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme={k.is_active ? 'green' : 'red'}>
                    {k.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td><Text fontSize="xs" color="gray.500" isTruncated maxW="200px">{k.description}</Text></Td>
                <Td>
                  <Text fontSize="xs" color="gray.500">
                    {k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}
                  </Text>
                </Td>
                <Td>
                  <Button size="xs" colorScheme="red" variant="ghost" onClick={() => deleteKey(k.id, k.name)}>
                    <FiTrash2 />
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Create Key Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create API Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="My API Key" />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What this key is used for..." />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={createKey}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default ApiKeys;
